import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTrItem, AppTranslate } from "./AppTranslate";
import { AppIcons, IIconDoc, IIconNode } from "./AppIcons";
import { AppLinkNodes, ILinkDoc, ILinkNode } from "./AppLinks";
import { AppTranslateRules } from "./AppTranslateRules";

// AppTranslateBubble - puts TRANSLATION'S MARKUP on a bubble in the open conversation, node by node: the flag that
// shows the original (or what was sent), the hidden original with its Copy, and - for a translated send that could
// not be translated - "Not sent - couldn't translate" with Retry / Send in English.
//
// ⭐ THE SAME MARKUP THE ARCHIVE PAGE WRITES (`AppTranslate`'s classes: `.b.tr`, `span.tro > span.trt + button.trc`,
// `button.trf` beside the clock, `tron` when open), so ONE stylesheet (`AppTranslate.Css`, in the frame's sheet) and
// ONE listener (`AppTranslateFrame`) serve the page's own bubbles, the app's sent bubbles, live ones and patched ones.
// ⛔ TEXT BY `textContent` ONLY - an original is somebody's message, and a `<b>` in it is a `<b>` they typed.
// ⛔ NO INLINE STYLE (the CSP) - classes only; hiding is the `hidden` attribute.
// ⚠ SHAPED BY INTERFACES (like `AppChatAppend`), so the suite drives it with a small fake document.

/** The node members this touches - a real `Element` fits by shape. */
export interface ITrNode extends IIconNode {
    className: string;
    textContent: string | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
    appendChild(Child: ITrNode | IIconNode): unknown;
    insertBefore(Child: ITrNode, Ref: ITrNode | null): unknown;
    querySelector(Selector: string): ITrNode | null;
    querySelectorAll(Selector: string): ArrayLike<ITrNode>;
}

/** The document members this touches. `createElementNS` is optional: a fake without it gets a labelled button. */
export interface ITrDoc {
    createElement(Tag: string): ITrNode;
    createElementNS?(Ns: string, Tag: string): IIconNode;
    querySelector(Selector: string): ITrNode | null;
    querySelectorAll(Selector: string): ArrayLike<ITrNode>;
}

export class AppTranslateBubble {
    /** On the failed bubble's two buttons: `retry` or `plain`. */
    public static readonly ActAttr: string = "data-wac-tr";
    public static readonly FailClass: string = "trfail";
    public static readonly StateClass: string = "wac-state";

    /** Has this class (a class list compared as words, never a selector built from data). */
    public static HasClass = (Node: ITrNode, Name: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return (" " + nz(Node.className) + " ").indexOf(" " + Name + " ") >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.HasClass");
            return false;
        }
    };

    private static AddClass = (Node: ITrNode, Name: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppTranslateBubble.HasClass(Node, Name) === false) { Node.className = (nz(Node.className) + " " + Name).trim(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.AddClass");
        }
    };

    private static DropClass = (Node: ITrNode, Name: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Kept: string[] = [];
            for (const One of nz(Node.className).split(/\s+/)) { if (One.length > 0 && One !== Name) { Kept.push(One); } }
            Node.className = Kept.join(" ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.DropClass");
        }
    };

    /**
     * ⭐ MARK A BUBBLE AS TRANSLATED: `Original` behind the flag. `Out` - the owner's own sent message, whose flag
     * reveals what WENT OUT ("Show what was sent"). A bubble already marked only gets the new original. "" = nothing.
     */
    public static Mark = (Doc: ITrDoc, Bubble: ITrNode, Original: string, Out: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(Original).length === 0) { return; }
            AppTranslateBubble.AddClass(Bubble, AppTranslate.BubbleClass);
            const Known: ITrNode | null = Bubble.querySelector("." + AppTranslate.OriginalTextClass);
            if (Known !== null) { Known.textContent = Original; return; }
            const Box: ITrNode = Doc.createElement("span");
            Box.className = AppTranslate.OriginalClass;
            const Words: ITrNode = Doc.createElement("span");
            Words.className = AppTranslate.OriginalTextClass;
            Words.textContent = Original;
            const Copy: ITrNode = AppTranslateBubble.Button(Doc, AppTranslate.CopyClass, AppTranslate.CopyLabel);
            Box.appendChild(Words);
            Box.appendChild(Copy);
            Bubble.insertBefore(Box, AppTranslateBubble.AfterText(Bubble));
            Bubble.appendChild(AppTranslateBubble.Flag(Doc, Out));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Mark");
        }
    };

    /** Where the original goes: before the status line, else before the clock, else at the end. */
    private static AfterText = (Bubble: ITrNode): ITrNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const State: ITrNode | null = Bubble.querySelector("." + AppTranslateBubble.StateClass);
            if (State !== null) { return State; }
            return Bubble.querySelector(".t");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.AfterText");
            return null;
        }
    };

    /** A real `<button type="button">` with its words (keyboard reachable by being a button). */
    private static Button = (Doc: ITrDoc, Cls: string, Words: string): ITrNode => {
        const Out: ITrNode = Doc.createElement("button");
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.setAttribute("type", "button");
            Out.className = Cls;
            Out.textContent = Words;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Button");
            return Out;
        }
    };

    /** The flag button: the shared `AppIcons.Flag`, labelled for a screen reader, `aria-expanded` false. */
    public static Flag = (Doc: ITrDoc, Out: boolean): ITrNode => {
        const Made: ITrNode = Doc.createElement("button");
        if (getCoreUtils().KErrorMode === true) { return Made; }
        try {
            let Label: string = AppTranslate.FlagLabel;
            if (Out === true) { Label = AppTranslate.FlagLabelOut; }
            Made.setAttribute("type", "button");
            Made.className = AppTranslate.FlagClass;
            Made.setAttribute("aria-label", Label);
            Made.setAttribute("title", Label);
            Made.setAttribute("aria-expanded", "false");
            if (typeof Doc.createElementNS === "function") {
                const Icon: IIconNode | null = AppIcons.Build(Doc as unknown as IIconDoc, AppIcons.Flag, 15);
                if (Icon !== null) { Made.appendChild(Icon); }
            }
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Flag");
            return Made;
        }
    };

    /** The flag was pressed: the original shows (or hides). Answers whether it is now shown. */
    public static Toggle = (Bubble: ITrNode, Flag: ITrNode): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Open: boolean = AppTranslateBubble.HasClass(Bubble, AppTranslate.OpenClass) === false;
            if (Open === true) { AppTranslateBubble.AddClass(Bubble, AppTranslate.OpenClass); }
            else { AppTranslateBubble.DropClass(Bubble, AppTranslate.OpenClass); }
            Flag.setAttribute("aria-expanded", String(Open));
            return Open;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Toggle");
            return false;
        }
    };

    /**
     * ⭐ "TRANSLATE EARLIER MESSAGES" ARRIVED: each item with a translation is put on the bubble with that `data-mid`
     * (compared in a loop, never in a selector) - its words replaced by the translation, the old words behind the
     * flag. A bubble already translated, one not on the page, or an item needing nothing is left alone. Answers how
     * many bubbles changed.
     */
    public static Patch = (Doc: ITrDoc, Items: AppTrItem[]): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const ById: Record<string, ITrNode> = {};
            const Found: ArrayLike<ITrNode> = Doc.querySelectorAll("[data-mid]");
            for (let At: number = 0; At < Found.length; At = At + 1) { ById[nz(Found[At].getAttribute("data-mid"))] = Found[At]; }
            let Done: number = 0;
            for (const Item of Items) {
                if (Item.Needed !== true || Item.Text.length === 0) { continue; }
                if (Object.prototype.hasOwnProperty.call(ById, Item.Id) === false) { continue; }
                if (AppTranslateBubble.Retext(Doc, ById[Item.Id], Item.Text) === true) { Done = Done + 1; }
            }
            return Done;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Patch");
            return 0;
        }
    };

    /** One bubble's words become `Shown`, the old words go behind the flag. False = nothing changed. */
    private static Retext = (Doc: ITrDoc, Bubble: ITrNode, Shown: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppTranslateBubble.HasClass(Bubble, AppTranslate.BubbleClass) === true) { return false; }
            const Words: ITrNode | null = Bubble.querySelector(".txt");
            if (Words === null) { return false; }
            const Original: string = nz(Words.textContent);
            if (Original.length === 0 || Original === Shown) { return false; }
            // ⭐ The translation with its links, as the rebuilt page draws a translated bubble's shown text (`AppLinks`,
            // 2026-10-01) - a plain `textContent` here turned the page's own anchors back into text.
            AppLinkNodes.Fill(Doc as unknown as ILinkDoc, Words as unknown as ILinkNode, Shown);
            AppTranslateBubble.Mark(Doc, Bubble, Original, AppTranslateBubble.HasClass(Bubble, "me"));
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Retext");
            return false;
        }
    };

    /**
     * ⛔ A TRANSLATED SEND THAT COULD NOT BE TRANSLATED - NOTHING WAS SENT: the status line says so in the alarm
     * colour, and two buttons offer Retry and Send in English. Pressing either is the owner's new click (a new
     * request with a new id) - nothing here sends. Drawn once per bubble.
     */
    public static Failed = (Doc: ITrDoc, Bubble: ITrNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Line: ITrNode | null = Bubble.querySelector("." + AppTranslateBubble.StateClass);
            if (Line !== null) { Line.textContent = AppTranslate.FailedLine; Line.className = AppTranslateBubble.StateClass + " wac-bad"; }
            if (Bubble.querySelector("." + AppTranslateBubble.FailClass) !== null) { return; }
            const Row: ITrNode = Doc.createElement("span");
            Row.className = AppTranslateBubble.FailClass;
            const Retry: ITrNode = AppTranslateBubble.Button(Doc, "trretry", AppTranslateRules.RetryLabel);
            Retry.setAttribute(AppTranslateBubble.ActAttr, "retry");
            const Plain: ITrNode = AppTranslateBubble.Button(Doc, "trplain", AppTranslateRules.PlainLabel(AppTranslateBubble.LangOf(Doc)));
            Plain.setAttribute(AppTranslateBubble.ActAttr, "plain");
            Row.appendChild(Retry);
            Row.appendChild(Plain);
            Bubble.insertBefore(Row, Bubble.querySelector(".t"));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Failed");
        }
    };

    /** On the page's thread (`.chat`): the owner's own language for this chat (`translate.json` `myLang`), once known. */
    public static readonly LangAttr: string = "data-wac-mylang";

    /** The owner's language as the page was told it ("" = not known yet, or a document that cannot say). */
    public static LangOf = (Doc: ITrDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Root: ITrNode | null = Doc.querySelector(".chat");
            if (Root === null) { return ""; }
            return nz(Root.getAttribute(AppTranslateBubble.LangAttr));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.LangOf");
            return "";
        }
    };

    /**
     * ⭐ The chat's settings are known: the page is told the owner's language, and every failed bubble already drawn
     * gets its second button named after it (`AppTranslateRules.PlainLabel`). Text only (`textContent`); sends nothing.
     */
    public static NoteLang = (Doc: ITrDoc, MyLang: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Root: ITrNode | null = Doc.querySelector(".chat");
            if (Root === null) { return; }
            if (nz(Root.getAttribute(AppTranslateBubble.LangAttr)) === nz(MyLang)) { return; }
            Root.setAttribute(AppTranslateBubble.LangAttr, nz(MyLang));
            const Buttons: ArrayLike<ITrNode> = Doc.querySelectorAll(".trplain");
            for (let At: number = 0; At < Buttons.length; At = At + 1) { Buttons[At].textContent = AppTranslateRules.PlainLabel(MyLang); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.NoteLang");
        }
    };

    /** The owner pressed Retry or Send in English and it went: the old bubble stops offering them. */
    public static Settled = (Bubble: ITrNode, Words: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: ITrNode | null = Bubble.querySelector("." + AppTranslateBubble.FailClass);
            if (Row !== null) { Row.setAttribute("hidden", ""); }
            const Line: ITrNode | null = Bubble.querySelector("." + AppTranslateBubble.StateClass);
            if (Line !== null) { Line.textContent = nz(Words); Line.className = AppTranslateBubble.StateClass; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBubble.Settled");
        }
    };

    /** The frame's CSS: the shared translated-bubble look plus the failed buttons and the earlier link.
     *  DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal. */
    public static Css = (): string => {
        // ⭐ THE FLAG SITS BESIDE THE CLOCK (the clock is `position:absolute` bottom-right, so an in-flow flag after it
        // would open a line of its own under the text); the bubble is wide enough for both.
        return AppTranslate.Css()
            + ".b.tr{min-width:92px}.b.tr .trf{position:absolute;right:44px;bottom:3px;margin:0}"
            + ".b .trfail{display:flex;flex-wrap:wrap;gap:6px;margin:6px 0 2px}"
            + ".b .trfail[hidden]{display:none}"
            + ".b .trfail button{padding:3px 10px;border:1px solid currentColor;border-radius:4px;background:transparent;"
            + "color:inherit;font:inherit;font-size:12.5px;cursor:pointer}"
            // ⭐ The business look (2026-10-13): an edged rectangle in the app's colours (it was a shadowed pill).
            + ".trearlier{align-self:center;margin:10px auto;padding:5px 14px;border:1px solid var(--line-strong,#858c99);"
            + "border-radius:6px;cursor:pointer;font:inherit;font-size:13px;font-weight:600;background:var(--bg,#fff);"
            + "color:var(--acc,#095495)}"
            + ".trearlier[disabled]{opacity:.7;cursor:default}"
            + ".trearlier[hidden]{display:none}";
    };
}
